/**
 * Teen Patti Store - Game state management
 * Three-card poker variant with Blind/Seen betting and Side Shows
 * Supports Dealer's Choice variations: Classic, Muflis, AK47, Slutty Queens, 
 * One-Eyed Jacks, Lowest Card Joker, Random Joker, 999, and Community cards
 */

import { create } from 'zustand';
import { 
  HAND_TYPES, 
  DEALER_CHOICE_VARIATIONS, 
  evaluateHand, 
  compareHands, 
  markWildcards,
  getVariationsByCategory 
} from './teenPattiEvaluator';

// Card suits and their properties
const SUITS = ['hearts', 'diamonds', 'clubs', 'spades'];
const SUIT_SYMBOLS = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
const SUIT_COLORS = { hearts: 'red', diamonds: 'red', clubs: 'black', spades: 'black' };

const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const RANK_VALUES = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 };

// Export variations from evaluator (backwards compatibility)
const VARIATIONS = Object.keys(DEALER_CHOICE_VARIATIONS).reduce((acc, key) => {
  acc[key] = key;
  return acc;
}, {});

// Player states
const PLAYER_STATUS = {
  ACTIVE_BLIND: 'ACTIVE_BLIND',
  ACTIVE_SEEN: 'ACTIVE_SEEN',
  PACKED: 'PACKED',
  WAITING: 'WAITING'
};

// Game phases
const GAME_PHASE = {
  WAITING: 'WAITING',
  POSTING_BOOTS: 'POSTING_BOOTS',
  DEALING: 'DEALING',
  BETTING: 'BETTING',
  SIDE_SHOW: 'SIDE_SHOW',
  SHOW: 'SHOW',
  SETTLING: 'SETTLING'
};

// AK47 joker ranks (for backwards compatibility)
const AK47_JOKERS = [14, 13, 4, 7]; // A, K, 4, 7

// Create a fresh deck
const createDeck = () => {
  const deck = [];
  let id = 0;
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        id: id++,
        suit,
        rank,
        value: RANK_VALUES[rank],
        symbol: SUIT_SYMBOLS[suit],
        color: SUIT_COLORS[suit],
        isJoker: false // Will be set based on variation
      });
    }
  }
  return deck;
};

// Fisher-Yates shuffle
const shuffleDeck = (deck) => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// AI player names
const AI_NAMES = ['Ravi', 'Priya', 'Amit', 'Neha', 'Vikram', 'Anjali', 'Rahul', 'Meera'];

// Table configuration defaults
const DEFAULT_CONFIG = {
  miniBoot: 10,
  boot: 20,
  minBuyIn: 500,
  maxBuyIn: 5000,
  blindBetMin: 1, // multiplier of current stake
  blindBetMax: 2,
  seenBetMin: 2,
  seenBetMax: 4,
  maxPot: null, // null = no limit, or a number like 1000
  maxBlindTurns: null, // no limit
  sideShowEnabled: true,
  sideShowCost: 0, // additional cost to request
  showCostBlind: 1, // multiplier
  showCostSeen: 2,
  variation: 'CLASSIC'
};

// Initial state
const INITIAL_STATE = {
  // Game configuration
  config: { ...DEFAULT_CONFIG },
  
  // Current variation (for display)
  variation: 'CLASSIC',
  
  // Players (including human at index 0)
  players: [],
  
  // Game state
  gamePhase: GAME_PHASE.WAITING,
  deck: [],
  pot: 0,
  currentStake: 0, // The current betting stake
  currentPlayerIndex: -1,
  buttonIndex: 0,
  miniBootIndex: 1,
  bootIndex: 2,
  
  // Side show state
  sideShowRequest: null, // { fromIndex, toIndex }
  
  // Round tracking
  roundNumber: 0,
  actionHistory: [],
  
  // Animation states
  dealingAnimation: false,
  chipAnimation: null, // { fromPlayer, amount }
  
  // Winner
  winner: null,
  showdownHands: null,
  
  // Pot limit reached
  potLimitReached: false,
  
  // Message
  message: '',
  
  // Dealer's Choice - revealed joker card (for Random Joker, Next Card Joker)
  revealedJoker: null,
  
  // Community cards (for community variations)
  communityCards: [],
  
  // Variation config from evaluator
  variationConfig: null
};

const useTeenPattiStore = create((set, get) => ({
  ...INITIAL_STATE,
  
  // Set game variation
  setVariation: (variation) => {
    const config = DEALER_CHOICE_VARIATIONS[variation];
    set({ 
      variation,
      variationConfig: config,
      config: { ...get().config, variation }
    });
  },
  
  // Set pot limit
  setPotLimit: (maxPot) => {
    set({
      config: { ...get().config, maxPot }
    });
  },
  
  // Get variation config
  getVariationConfig: () => {
    const { variation } = get();
    return DEALER_CHOICE_VARIATIONS[variation] || DEALER_CHOICE_VARIATIONS.CLASSIC;
  },
  
  // Initialize game with players
  initGame: (numPlayers = 4, startingChips = 1000, variation = 'CLASSIC') => {
    const players = [];
    const variationConfig = DEALER_CHOICE_VARIATIONS[variation];
    
    // Human player
    players.push({
      id: 0,
      name: 'You',
      isHuman: true,
      chips: startingChips,
      cards: [],
      holeCards: [], // For community variations
      status: PLAYER_STATUS.WAITING,
      currentBet: 0,
      totalBet: 0,
      isButton: false,
      isMiniBootPosition: false,
      isBootPosition: false
    });
    
    // AI players
    const shuffledNames = [...AI_NAMES].sort(() => Math.random() - 0.5);
    for (let i = 1; i < numPlayers; i++) {
      players.push({
        id: i,
        name: shuffledNames[i - 1],
        isHuman: false,
        chips: startingChips,
        cards: [],
        holeCards: [],
        status: PLAYER_STATUS.WAITING,
        currentBet: 0,
        totalBet: 0,
        isButton: false,
        isMiniBootPosition: false,
        isBootPosition: false
      });
    }
    
    set({
      ...INITIAL_STATE,
      players,
      variation,
      variationConfig,
      config: { ...DEFAULT_CONFIG, variation },
      gamePhase: GAME_PHASE.WAITING
    });
  },
  
  // Start a new hand with dealing animation
  startHand: () => {
    const state = get();
    const { players, buttonIndex, config, variation } = state;
    const variationConfig = DEALER_CHOICE_VARIATIONS[variation];
    
    // Reset players for new hand
    const activePlayers = players.filter(p => p.chips >= config.boot);
    if (activePlayers.length < 2) {
      set({ message: 'Not enough players with chips to continue' });
      return;
    }
    
    const numPlayers = players.length;
    const miniBootIdx = (buttonIndex + 1) % numPlayers;
    const bootIdx = (buttonIndex + 2) % numPlayers;
    const firstToActIdx = (buttonIndex + 3) % numPlayers;
    
    // Create and shuffle deck
    let deck = shuffleDeck(createDeck());
    
    // Determine if this is a community variation
    const isCommunity = variationConfig?.category === 'community';
    const holeCardCount = isCommunity ? (variationConfig.holeCards || 2) : 3;
    const communityCount = isCommunity ? (variationConfig.communityCards || 0) : 0;
    
    // Handle revealed joker variations (Random Joker, Next Card Joker)
    let revealedJoker = null;
    if (variationConfig?.wildcardType === 'random_revealed' || variationConfig?.wildcardType === 'next_card') {
      // Take one card from deck as the revealed joker
      revealedJoker = deck.shift();
    }
    
    // Start dealing animation
    set({ dealingAnimation: true, gamePhase: GAME_PHASE.DEALING });
    
    // Deal cards to each player and set positions
    let cardIndex = 0;
    const newPlayers = players.map((p, idx) => {
      const cards = deck.slice(cardIndex, cardIndex + holeCardCount);
      cardIndex += holeCardCount;
      
      // Mark wildcards on the cards
      const markedCards = markWildcards(
        isCommunity ? cards : cards.slice(0, 3), 
        variation, 
        revealedJoker
      );
      
      // For non-community, pad to 3 cards if needed
      const finalCards = isCommunity ? markedCards : markedCards.slice(0, 3);
      
      return {
        ...p,
        cards: finalCards,
        holeCards: isCommunity ? markedCards : [],
        status: p.chips >= config.boot ? PLAYER_STATUS.ACTIVE_BLIND : PLAYER_STATUS.PACKED,
        currentBet: 0,
        totalBet: 0,
        isButton: idx === buttonIndex,
        isMiniBootPosition: idx === miniBootIdx,
        isBootPosition: idx === bootIdx
      };
    });
    
    // Deal community cards if applicable
    let communityCards = [];
    if (isCommunity && communityCount > 0) {
      communityCards = deck.slice(cardIndex, cardIndex + communityCount);
      cardIndex += communityCount;
    }
    
    // Post forced bets
    if (newPlayers[miniBootIdx].status !== PLAYER_STATUS.PACKED) {
      newPlayers[miniBootIdx].chips -= config.miniBoot;
      newPlayers[miniBootIdx].currentBet = config.miniBoot;
      newPlayers[miniBootIdx].totalBet = config.miniBoot;
    }
    
    if (newPlayers[bootIdx].status !== PLAYER_STATUS.PACKED) {
      newPlayers[bootIdx].chips -= config.boot;
      newPlayers[bootIdx].currentBet = config.boot;
      newPlayers[bootIdx].totalBet = config.boot;
    }
    
    const initialPot = config.miniBoot + config.boot;
    
    // Delay for dealing animation then set up game
    setTimeout(() => {
      set({
        players: newPlayers,
        deck: deck.slice(cardIndex),
        pot: initialPot,
        currentStake: config.boot,
        currentPlayerIndex: firstToActIdx,
        gamePhase: GAME_PHASE.BETTING,
        dealingAnimation: false,
        potLimitReached: config.maxPot ? initialPot >= config.maxPot : false,
        roundNumber: state.roundNumber + 1,
        revealedJoker,
        communityCards,
        variationConfig,
        actionHistory: [
          { player: newPlayers[miniBootIdx].name, action: 'posts Mini-Boot', amount: config.miniBoot },
          { player: newPlayers[bootIdx].name, action: 'posts Boot', amount: config.boot }
        ],
        winner: null,
        showdownHands: null,
        message: `${newPlayers[firstToActIdx].name}'s turn`
      });
      
      // If first player is AI, trigger their action
      if (!newPlayers[firstToActIdx].isHuman) {
        setTimeout(() => get().aiAction(), 1000);
      }
    }, 800); // Dealing animation duration
  },
  
  // See cards (become Seen)
  seeCards: () => {
    const state = get();
    const { players, currentPlayerIndex } = state;
    
    if (currentPlayerIndex < 0) return;
    const player = players[currentPlayerIndex];
    if (!player.isHuman || player.status !== PLAYER_STATUS.ACTIVE_BLIND) return;
    
    const newPlayers = [...players];
    newPlayers[currentPlayerIndex] = {
      ...player,
      status: PLAYER_STATUS.ACTIVE_SEEN
    };
    
    set({
      players: newPlayers,
      actionHistory: [...state.actionHistory, { player: player.name, action: 'sees cards' }]
    });
  },
  
  // Place a bet (Blind or Chaal)
  placeBet: (amount) => {
    const state = get();
    const { players, currentPlayerIndex, currentStake, pot, config } = state;
    
    if (currentPlayerIndex < 0 || state.gamePhase !== GAME_PHASE.BETTING) return;
    const player = players[currentPlayerIndex];
    if (!player.isHuman) return;
    
    const isBlind = player.status === PLAYER_STATUS.ACTIVE_BLIND;
    let minBet = isBlind ? currentStake * config.blindBetMin : currentStake * config.seenBetMin;
    let maxBet = isBlind ? currentStake * config.blindBetMax : currentStake * config.seenBetMax;
    
    // Apply pot limit if set
    if (config.maxPot && pot + maxBet > config.maxPot) {
      maxBet = Math.max(minBet, config.maxPot - pot);
    }
    
    // Validate bet
    const actualBet = Math.min(Math.max(amount, minBet), Math.min(maxBet, player.chips));
    
    const newPlayers = [...players];
    newPlayers[currentPlayerIndex] = {
      ...player,
      chips: player.chips - actualBet,
      currentBet: actualBet,
      totalBet: player.totalBet + actualBet
    };
    
    // Update stake based on bet
    const newStake = isBlind ? actualBet : Math.ceil(actualBet / 2);
    const newPot = pot + actualBet;
    const potLimitReached = config.maxPot ? newPot >= config.maxPot : false;
    
    // Trigger chip animation
    set({
      chipAnimation: { fromPlayer: currentPlayerIndex, amount: actualBet }
    });
    
    setTimeout(() => {
      set({
        players: newPlayers,
        pot: newPot,
        currentStake: newStake,
        potLimitReached,
        chipAnimation: null,
        actionHistory: [...get().actionHistory, { 
          player: player.name, 
          action: isBlind ? 'bets blind' : 'chaal', 
          amount: actualBet 
        }]
      });
      
      // If pot limit reached, force show
      if (potLimitReached) {
        get().forcePotLimitShow();
      } else {
        get().nextPlayer();
      }
    }, 400); // Chip animation duration
  },
  
  // Force show when pot limit is reached
  forcePotLimitShow: () => {
    const state = get();
    const { players, pot, variation, revealedJoker, communityCards, variationConfig } = state;
    
    const activePlayers = players.filter(p => 
      p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
    
    if (activePlayers.length < 2) {
      get().checkForWinner();
      return;
    }
    
    // Evaluate all hands
    const hands = {};
    let bestScore = -1;
    let winnerIdx = -1;
    
    const isCommunity = variationConfig?.category === 'community';
    
    activePlayers.forEach(p => {
      const idx = players.indexOf(p);
      const hand = isCommunity
        ? evaluateHand(p.cards, variation, { holeCards: p.holeCards, communityCards, revealedJoker })
        : evaluateHand(p.cards, variation, { revealedJoker });
      hands[idx] = hand;
      if (hand && hand.score > bestScore) {
        bestScore = hand.score;
        winnerIdx = idx;
      }
    });
    
    const newPlayers = [...players];
    newPlayers[winnerIdx] = {
      ...newPlayers[winnerIdx],
      chips: newPlayers[winnerIdx].chips + pot
    };
    
    set({
      players: newPlayers,
      pot: 0,
      gamePhase: GAME_PHASE.SETTLING,
      winner: newPlayers[winnerIdx],
      showdownHands: hands,
      message: `Pot Limit reached! ${newPlayers[winnerIdx].name} wins with ${hands[winnerIdx]?.name || 'best hand'}!`
    });
  },
  
  // Pack (fold)
  pack: () => {
    const state = get();
    const { players, currentPlayerIndex } = state;
    
    if (currentPlayerIndex < 0) return;
    const player = players[currentPlayerIndex];
    if (!player.isHuman) return;
    
    const newPlayers = [...players];
    newPlayers[currentPlayerIndex] = {
      ...player,
      status: PLAYER_STATUS.PACKED
    };
    
    set({
      players: newPlayers,
      actionHistory: [...state.actionHistory, { player: player.name, action: 'packs' }]
    });
    
    get().checkForWinner();
  },
  
  // Request a side show
  requestSideShow: () => {
    const state = get();
    const { players, currentPlayerIndex } = state;
    
    if (currentPlayerIndex < 0 || !state.config.sideShowEnabled) return;
    const player = players[currentPlayerIndex];
    if (!player.isHuman || player.status !== PLAYER_STATUS.ACTIVE_SEEN) return;
    
    // Find previous active seen player
    const activePlayers = players.filter(p => 
      p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
    if (activePlayers.length <= 2) return; // Can only side show with >2 players
    
    // Find previous seen player
    let prevSeenIdx = -1;
    for (let i = 1; i < players.length; i++) {
      const idx = (currentPlayerIndex - i + players.length) % players.length;
      if (players[idx].status === PLAYER_STATUS.ACTIVE_SEEN) {
        prevSeenIdx = idx;
        break;
      }
    }
    
    if (prevSeenIdx === -1) return;
    
    set({
      gamePhase: GAME_PHASE.SIDE_SHOW,
      sideShowRequest: { fromIndex: currentPlayerIndex, toIndex: prevSeenIdx },
      message: `${player.name} requests Side Show with ${players[prevSeenIdx].name}`
    });
    
    // AI responds to side show
    if (!players[prevSeenIdx].isHuman) {
      setTimeout(() => get().respondToSideShow(Math.random() > 0.3), 1500);
    }
  },
  
  // Respond to side show request
  respondToSideShow: (accepted) => {
    const state = get();
    const { players, sideShowRequest, variation, revealedJoker } = state;
    
    if (!sideShowRequest) return;
    
    const requester = players[sideShowRequest.fromIndex];
    const responder = players[sideShowRequest.toIndex];
    
    if (!accepted) {
      set({
        gamePhase: GAME_PHASE.BETTING,
        sideShowRequest: null,
        actionHistory: [...state.actionHistory, { 
          player: responder.name, 
          action: 'declines Side Show' 
        }],
        message: `${responder.name} declines Side Show`
      });
      get().nextPlayer();
      return;
    }
    
    // Compare hands privately
    const hand1 = evaluateHand(requester.cards, variation, { revealedJoker });
    const hand2 = evaluateHand(responder.cards, variation, { revealedJoker });
    const result = compareHands(hand1, hand2);
    
    // Loser is eliminated (requester loses ties)
    const loserIdx = result <= 0 ? sideShowRequest.fromIndex : sideShowRequest.toIndex;
    const loser = players[loserIdx];
    
    const newPlayers = [...players];
    newPlayers[loserIdx] = { ...loser, status: PLAYER_STATUS.PACKED };
    
    set({
      players: newPlayers,
      gamePhase: GAME_PHASE.BETTING,
      sideShowRequest: null,
      actionHistory: [...state.actionHistory, { 
        player: responder.name, 
        action: 'accepts Side Show' 
      }, {
        player: loser.name,
        action: 'loses Side Show'
      }],
      message: `${loser.name} loses the Side Show`
    });
    
    get().checkForWinner();
  },
  
  // Request final show (when 2 players remain)
  requestShow: () => {
    const state = get();
    const { players, currentPlayerIndex, currentStake, pot, config, variation, revealedJoker, communityCards, variationConfig } = state;
    
    if (currentPlayerIndex < 0) return;
    const player = players[currentPlayerIndex];
    
    const activePlayers = players.filter(p => 
      p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
    if (activePlayers.length !== 2) return;
    
    // Pay show cost
    const isBlind = player.status === PLAYER_STATUS.ACTIVE_BLIND;
    const showCost = isBlind ? currentStake * config.showCostBlind : currentStake * config.showCostSeen;
    const actualCost = Math.min(showCost, player.chips);
    
    const newPlayers = [...players];
    newPlayers[currentPlayerIndex] = {
      ...player,
      chips: player.chips - actualCost,
      totalBet: player.totalBet + actualCost
    };
    
    // Find opponent
    const opponentIdx = players.findIndex((p, idx) => 
      idx !== currentPlayerIndex && 
      (p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN)
    );
    
    const isCommunity = variationConfig?.category === 'community';
    
    // Evaluate hands
    const hand1 = isCommunity
      ? evaluateHand(player.cards, variation, { holeCards: player.holeCards, communityCards, revealedJoker })
      : evaluateHand(player.cards, variation, { revealedJoker });
    const hand2 = isCommunity
      ? evaluateHand(players[opponentIdx].cards, variation, { holeCards: players[opponentIdx].holeCards, communityCards, revealedJoker })
      : evaluateHand(players[opponentIdx].cards, variation, { revealedJoker });
    const result = compareHands(hand1, hand2);
    
    // Requester loses ties
    const winnerIdx = result > 0 ? currentPlayerIndex : 
                      result < 0 ? opponentIdx : opponentIdx;
    
    const winnerPlayer = players[winnerIdx];
    newPlayers[winnerIdx] = {
      ...newPlayers[winnerIdx],
      chips: newPlayers[winnerIdx].chips + pot + actualCost
    };
    
    set({
      players: newPlayers,
      pot: 0,
      gamePhase: GAME_PHASE.SETTLING,
      winner: winnerPlayer,
      showdownHands: {
        [currentPlayerIndex]: hand1,
        [opponentIdx]: hand2
      },
      actionHistory: [...state.actionHistory, { 
        player: player.name, 
        action: 'requests Show',
        amount: actualCost
      }],
      message: `${winnerPlayer.name} wins with ${winnerIdx === currentPlayerIndex ? hand1?.name : hand2?.name}!`
    });
  },
  
  // Move to next player
  nextPlayer: () => {
    const state = get();
    const { players, currentPlayerIndex } = state;
    
    // Find next active player
    let nextIdx = currentPlayerIndex;
    for (let i = 1; i <= players.length; i++) {
      nextIdx = (currentPlayerIndex + i) % players.length;
      const p = players[nextIdx];
      if (p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN) {
        break;
      }
    }
    
    set({
      currentPlayerIndex: nextIdx,
      message: `${players[nextIdx].name}'s turn`
    });
    
    // Check for winner first
    const stillActive = players.filter(p => 
      p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
    
    if (stillActive.length === 1) {
      get().declareWinner(players.indexOf(stillActive[0]));
      return;
    }
    
    // Trigger AI action
    if (!players[nextIdx].isHuman) {
      setTimeout(() => get().aiAction(), 1000);
    }
  },
  
  // Check if only one player remains
  checkForWinner: () => {
    const { players } = get();
    const activePlayers = players.filter(p => 
      p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
    
    if (activePlayers.length === 1) {
      const winnerIdx = players.indexOf(activePlayers[0]);
      get().declareWinner(winnerIdx);
    } else {
      get().nextPlayer();
    }
  },
  
  // Declare winner
  declareWinner: (winnerIdx) => {
    const state = get();
    const { players, pot } = state;
    
    const newPlayers = [...players];
    newPlayers[winnerIdx] = {
      ...newPlayers[winnerIdx],
      chips: newPlayers[winnerIdx].chips + pot
    };
    
    set({
      players: newPlayers,
      pot: 0,
      gamePhase: GAME_PHASE.SETTLING,
      winner: newPlayers[winnerIdx],
      message: `${newPlayers[winnerIdx].name} wins ₹${pot}!`
    });
  },
  
  // AI decision making
  aiAction: () => {
    const state = get();
    const { players, currentPlayerIndex, currentStake, config, gamePhase, variation, revealedJoker } = state;
    
    if (gamePhase !== GAME_PHASE.BETTING) return;
    if (currentPlayerIndex < 0) return;
    
    const player = players[currentPlayerIndex];
    if (player.isHuman || player.status === PLAYER_STATUS.PACKED) return;
    
    const hand = evaluateHand(player.cards, variation, { revealedJoker });
    const handStrength = hand ? hand.type.rank / 6 : 0.3; // 0-1 scale
    
    const isBlind = player.status === PLAYER_STATUS.ACTIVE_BLIND;
    const activePlayers = players.filter(p => 
      p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
    
    // Decide to see cards (stronger hands more likely to see early)
    if (isBlind && Math.random() < handStrength * 0.7 + 0.1) {
      const newPlayers = [...players];
      newPlayers[currentPlayerIndex] = { ...player, status: PLAYER_STATUS.ACTIVE_SEEN };
      set({ 
        players: newPlayers,
        actionHistory: [...state.actionHistory, { player: player.name, action: 'sees cards' }]
      });
    }
    
    const updatedPlayer = get().players[currentPlayerIndex];
    const nowBlind = updatedPlayer.status === PLAYER_STATUS.ACTIVE_BLIND;
    
    // Check for show (2 players)
    if (activePlayers.length === 2 && Math.random() < handStrength * 0.5 + 0.2) {
      get().requestShow();
      return;
    }
    
    // Decide action based on hand strength
    const packChance = (1 - handStrength) * 0.3;
    
    if (Math.random() < packChance && handStrength < 0.4) {
      // Pack weak hands sometimes
      const newPlayers = [...get().players];
      newPlayers[currentPlayerIndex] = { ...newPlayers[currentPlayerIndex], status: PLAYER_STATUS.PACKED };
      set({
        players: newPlayers,
        actionHistory: [...get().actionHistory, { player: player.name, action: 'packs' }]
      });
      get().checkForWinner();
      return;
    }
    
    // Place bet
    const minBet = nowBlind ? currentStake * config.blindBetMin : currentStake * config.seenBetMin;
    const maxBet = nowBlind ? currentStake * config.blindBetMax : currentStake * config.seenBetMax;
    
    // Stronger hands bet more
    const betMultiplier = 0.5 + handStrength * 0.5;
    let betAmount = Math.floor(minBet + (maxBet - minBet) * betMultiplier);
    betAmount = Math.min(betAmount, updatedPlayer.chips);
    
    const newPlayers = [...get().players];
    newPlayers[currentPlayerIndex] = {
      ...newPlayers[currentPlayerIndex],
      chips: newPlayers[currentPlayerIndex].chips - betAmount,
      currentBet: betAmount,
      totalBet: newPlayers[currentPlayerIndex].totalBet + betAmount
    };
    
    const newStake = nowBlind ? betAmount : Math.ceil(betAmount / 2);
    
    set({
      players: newPlayers,
      pot: get().pot + betAmount,
      currentStake: newStake,
      actionHistory: [...get().actionHistory, { 
        player: player.name, 
        action: nowBlind ? 'bets blind' : 'chaal', 
        amount: betAmount 
      }]
    });
    
    get().nextPlayer();
  },
  
  // Advance to next hand
  nextHand: () => {
    const state = get();
    const { players, buttonIndex } = state;
    
    // Rotate button
    const newButtonIdx = (buttonIndex + 1) % players.length;
    
    // Reset player states
    const newPlayers = players.map(p => ({
      ...p,
      cards: [],
      holeCards: [],
      status: PLAYER_STATUS.WAITING,
      currentBet: 0,
      totalBet: 0,
      isButton: false,
      isMiniBootPosition: false,
      isBootPosition: false
    }));
    
    set({
      players: newPlayers,
      buttonIndex: newButtonIdx,
      gamePhase: GAME_PHASE.WAITING,
      winner: null,
      showdownHands: null,
      sideShowRequest: null,
      revealedJoker: null,
      communityCards: [],
      message: 'Click Deal to start next hand'
    });
  },
  
  // Reset game
  resetGame: () => {
    set(INITIAL_STATE);
  }
}));

export default useTeenPattiStore;
export { 
  HAND_TYPES, 
  PLAYER_STATUS, 
  GAME_PHASE, 
  VARIATIONS, 
  DEALER_CHOICE_VARIATIONS,
  evaluateHand, 
  compareHands,
  markWildcards,
  getVariationsByCategory,
  AK47_JOKERS 
};
